/**
 * VACACIONES Y TELETRABAJO (administración, 14/10/2026)
 * -----------------------------------------------------
 * La pestaña de «Registro de jornada» donde se apuntan los días de
 * VACACIONES, TELETRABAJO y DÍA LIBRE del equipo que no es médico
 * (administración, recepción, enfermería…). Los doctores van por Agendas
 * disponibles.
 *
 *  - Un calendario ANUAL con todos a la vez: cada día, las iniciales de quien
 *    tiene algo, en el color de lo que tiene (violeta vacaciones, azul
 *    teletrabajo, gris día libre), y la lista entera en el título. Con una
 *    persona elegida en «Persona», el día entero va de su color y un punto en
 *    la esquina dice que está puesto a mano.
 *  - Pinchar un día abre su ficha: quién tiene qué (y si es del patrón o
 *    puesto a mano, por quién) y, para apuntar, los botones de cada tipo,
 *    «Nada este día» (anula el patrón ese día) y «Volver al patrón».
 *  - «Marcar varios días»: un rango de golpe (unas vacaciones), con o sin
 *    sábados y domingos.
 *  - «Patrones»: «teletrabajo todos los viernes». Rellenan los días solos; un
 *    día puesto a mano no rompe el patrón.
 *
 * Quién: gestión apunta para cualquiera; los demás del equipo, solo para sí
 * (el servidor lo dice con `puede_gestionar` y `yo`, y lo vuelve a comprobar).
 * Quien no es de gestión ve de los compañeros solo quién y qué; de sí mismo,
 * todo. La regla del cálculo, en backend/services/ausencias_equipo.py.
 *
 * EL CUADRANTE (revisión del 14/10/2026; la decisión final es de
 * administración): los turnos «Vacaciones» y «Libre» del Cuadrante de cada
 * persona también salen aquí, de solo lectura: «Vacaciones (Cuadrante)»,
 * «Día libre (Cuadrante)». Si ese día hay algo apuntado aquí, manda lo de
 * aquí. Lo dice una línea encima del calendario.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { CalendarPlus, Loader2, Pencil, Plus, Power, RotateCcw } from 'lucide-react';
import { mensajeError } from '../../lib/api';
import { strToDate, todayStr } from '../../lib/citasApi';
import { DIAS_SEMANA, textoDias, textoFecha } from '../../lib/disponibilidad';
import {
  COLOR_TIPO, NINGUNO, ORIGEN_CUADRANTE, TEXTO_TIPO, TIPOS, cargarCalendario, claseTipo, crearPatron,
  desactivarPatron, editarPatron, iniciales, ponerDia, ponerRango, textoPersonas, textoTipo,
} from '../../lib/ausencias';
import RejillaAnual, { NavegacionAnio } from '../calendario/RejillaAnual';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';

// Cuántas iniciales caben en un día del calendario; con más, «+N».
const MAX_INICIALES = 3;

const conMayuscula = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t);
const fechaLarga = (f) => conMayuscula(format(strToDate(f), "EEEE d 'de' MMMM 'de' yyyy", { locale: es }));

/** «2099-01-12T08:30:00+00:00» → «12/01/2099» en hora de aquí. */
function diaDe(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('es-ES', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

function Muestra({ tipo, children }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-block w-4 h-4 rounded ${claseTipo(tipo)}`} aria-hidden="true" />
      {children}
    </span>
  );
}

/** Lo que tiene una persona un día, dicho para la ficha del día. */
function estadoTexto(e) {
  if (!e) return 'nada';
  // Del Cuadrante (solo lectura), o de un compañero visto por quien no es
  // de gestión (el servidor manda solo quién y qué).
  if (e.origen === ORIGEN_CUADRANTE || !('manual' in e)) return textoTipo(e) || 'Nada';
  if (!e.manual) return `${TEXTO_TIPO[e.tipo] || 'Nada'} (patrón)`;
  const quien = `${e.por ? `, por ${e.por}` : ''}${e.at ? ` el ${diaDe(e.at)}` : ''}`;
  return e.tipo ? `${TEXTO_TIPO[e.tipo]} (a mano${quien})` : `Nada (anulado a mano${quien})`;
}

const rangoVacio = (hoy, userId) => ({
  userId, tipo: 'vacaciones', desde: hoy, hasta: '', soloLaborables: true,
});
const patronVacio = (hoy, userId) => ({
  id: null, userId, tipo: 'teletrabajo', dias: [], desde: hoy, hasta: '', nota: '',
});

/** Un desplegable de personas, o el nombre si solo hay una. */
function ElegirPersona({ id, personas, valor, onCambiar, testId }) {
  if (personas.length === 1) {
    return (
      <p className="mt-1.5 text-sm font-medium text-[#13231E]" data-testid={testId}>{personas[0].nombre}</p>
    );
  }
  return (
    <select
      id={id}
      value={valor}
      onChange={(e) => onCambiar(e.target.value)}
      className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
      data-testid={testId}
    >
      <option value="">Elige a la persona…</option>
      {personas.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
    </select>
  );
}

export default function AusenciasTab() {
  const hoy = todayStr();
  const anioHoy = Number(hoy.slice(0, 4));
  const [anio, setAnio] = useState(anioHoy);
  const [filtro, setFiltro] = useState(''); // '' = todo el equipo
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [recarga, setRecarga] = useState(0);
  const [diaAbierto, setDiaAbierto] = useState(null); // 'aaaa-mm-dd'
  const [paraQuien, setParaQuien] = useState('');
  const [guardandoDia, setGuardandoDia] = useState(false);
  const [rango, setRango] = useState(null);
  const [guardandoRango, setGuardandoRango] = useState(false);
  const [patron, setPatron] = useState(null);
  const [guardandoPatron, setGuardandoPatron] = useState(false);

  useEffect(() => {
    let cancelado = false;
    setCargando(true);
    cargarCalendario(anio)
      .then((d) => { if (!cancelado) setDatos(d && typeof d === 'object' && !Array.isArray(d) ? d : null); })
      .catch((e) => {
        if (cancelado) return;
        setDatos(null);
        toast.error(mensajeError(e, 'No se pudo cargar el calendario del equipo'));
      })
      .finally(() => { if (!cancelado) setCargando(false); });
    return () => { cancelado = true; };
  }, [anio, recarga]);

  // Lo que llegó, solo si es de este año (mientras carga otro, nada del anterior).
  const vigente = datos && datos.anio === anio ? datos : null;
  const miembros = useMemo(() => (Array.isArray(vigente?.miembros) ? vigente.miembros : []), [vigente]);
  const porId = useMemo(() => Object.fromEntries(miembros.map((m) => [m.id, m])), [miembros]);
  const puedeGestionar = !!vigente?.puede_gestionar;
  const yo = vigente?.yo || null;
  // A quién puede apuntarle días quien mira: gestión, a todos; los demás, a sí.
  const editables = useMemo(
    () => (puedeGestionar ? miembros : miembros.filter((m) => m.id === yo)),
    [puedeGestionar, miembros, yo],
  );
  const puedeApuntar = editables.length > 0;
  const dias = vigente?.dias || {};
  const elegido = filtro && porId[filtro] ? porId[filtro] : null;
  const patrones = (vigente?.patrones || []).filter((p) => !elegido || p.user_id === elegido.id);
  const resumen = elegido ? vigente?.resumen?.[elegido.id] : null;

  // `respaldo`: el nombre guardado (el de un patrón de alguien que ya no está
  // en el equipo).
  const nombreDe = (id, respaldo) => porId[id]?.nombre || respaldo || '—';
  const inicialesDe = (id) => porId[id]?.iniciales || iniciales(nombreDe(id));
  const visibles = (f) => (dias[f] || []).filter((e) => !elegido || e.user_id === elegido.id);
  // Para la persona a la que se apunta por defecto: la elegida, o uno mismo.
  const porDefecto = () => {
    if (elegido && editables.some((m) => m.id === elegido.id)) return elegido.id;
    if (editables.length === 1) return editables[0].id;
    return editables.some((m) => m.id === yo) ? yo : '';
  };
  const puedeTocar = (userId) => puedeGestionar || userId === yo;

  const abrirDia = (f) => {
    setParaQuien(porDefecto());
    setDiaAbierto(f);
  };

  const apuntarDia = async (tipo) => {
    if (!diaAbierto || !paraQuien) { toast.error('Elige a la persona'); return; }
    setGuardandoDia(true);
    try {
      await ponerDia(paraQuien, diaAbierto, tipo);
      const que = tipo === null ? 'vuelve al patrón' : tipo === NINGUNO ? 'sin nada ese día' : TEXTO_TIPO[tipo].toLowerCase();
      toast.success(`${nombreDe(paraQuien)} · ${textoFecha(diaAbierto)}: ${que}`);
      setDiaAbierto(null);
      setRecarga((n) => n + 1);
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo guardar el día'));
    } finally {
      setGuardandoDia(false);
    }
  };

  const guardarRango = async () => {
    if (!rango) return;
    if (!rango.userId) { toast.error('Elige a la persona'); return; }
    if (!rango.desde || !rango.hasta) { toast.error('Indica desde qué día y hasta qué día'); return; }
    if (rango.hasta < rango.desde) { toast.error('La fecha «hasta» no puede ser anterior a «desde»'); return; }
    setGuardandoRango(true);
    try {
      const r = await ponerRango(rango.userId, rango.desde, rango.hasta,
        rango.tipo === 'patron' ? null : rango.tipo, rango.soloLaborables);
      const n = Number(r?.cambiados) || 0;
      toast.success(n ? `${nombreDe(rango.userId)}: ${n} día${n === 1 ? '' : 's'} apuntado${n === 1 ? '' : 's'}`
        : 'Esos días ya estaban así');
      setRango(null);
      setRecarga((x) => x + 1);
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudieron guardar los días'));
    } finally {
      setGuardandoRango(false);
    }
  };

  const abrirAltaPatron = () => setPatron(patronVacio(hoy, porDefecto()));
  const abrirEdicionPatron = (p) => setPatron({
    id: p.id, userId: p.user_id, userNombre: p.user_nombre || '', tipo: p.tipo, dias: [...(p.dias_semana || [])], desde: p.desde || '',
    hasta: p.hasta || '', nota: p.nota || '',
  });
  const alternarDiaSemana = (d) => setPatron((f) => ({
    ...f, dias: f.dias.includes(d) ? f.dias.filter((x) => x !== d) : [...f.dias, d].sort((a, b) => a - b),
  }));

  const guardarPatron = async () => {
    if (!patron) return;
    if (!patron.userId) { toast.error('Elige a la persona'); return; }
    if (!patron.dias.length) { toast.error('Marca al menos un día de la semana'); return; }
    if (!patron.desde) { toast.error('Indica desde qué fecha vale el patrón'); return; }
    if (patron.hasta && patron.hasta < patron.desde) { toast.error('La fecha «hasta» no puede ser anterior a «desde»'); return; }
    const cuerpo = {
      tipo: patron.tipo, dias_semana: patron.dias, desde: patron.desde,
      hasta: patron.hasta || null, nota: patron.nota.trim(),
    };
    setGuardandoPatron(true);
    try {
      if (patron.id) await editarPatron(patron.id, cuerpo);
      else await crearPatron({ ...cuerpo, user_id: patron.userId });
      toast.success(patron.id ? 'Patrón guardado' : 'Patrón añadido: los días se han rellenado');
      setPatron(null);
      setRecarga((n) => n + 1);
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo guardar el patrón'));
    } finally {
      setGuardandoPatron(false);
    }
  };

  const activarPatron = async (p, activo) => {
    try {
      if (activo) {
        await editarPatron(p.id, {
          tipo: p.tipo, dias_semana: p.dias_semana, desde: p.desde, hasta: p.hasta || null,
          nota: p.nota || '', activo: true,
        });
      } else {
        await desactivarPatron(p.id);
      }
      toast.success(activo ? 'Patrón activado' : 'Patrón desactivado');
      setRecarga((n) => n + 1);
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo cambiar el patrón'));
    }
  };

  // La ficha del día: todos los que tienen algo y lo de la persona elegida.
  const delDia = diaAbierto ? (dias[diaAbierto] || []) : [];
  const deLaPersona = delDia.find((e) => e.user_id === paraQuien) || null;

  const renderDia = (f, nombreMes) => {
    const lista = visibles(f);
    const conTipo = lista.filter((e) => e.tipo);
    const numero = Number(f.slice(8, 10));
    const esHoy = f === hoy;
    const personas = conTipo.map((e) => ({ nombre: nombreDe(e.user_id), tipo: e.tipo, origen: e.origen }));
    const texto = textoPersonas(personas);
    const solo = elegido ? (lista[0] || null) : null;
    const manual = !!(solo && solo.manual);
    const fondo = solo && solo.tipo ? `${claseTipo(solo.tipo)} font-semibold` : 'text-[#13231E]/80';
    const sobran = conTipo.length > MAX_INICIALES ? conTipo.length - (MAX_INICIALES - 1) : 0;
    const chips = sobran ? conTipo.slice(0, MAX_INICIALES - 1) : conTipo;
    return (
      <button
        type="button"
        onClick={() => abrirDia(f)}
        data-testid={`aus-dia-${f}`}
        data-tipos={conTipo.map((e) => e.tipo).join(',')}
        data-manual={manual ? 'si' : 'no'}
        aria-label={`${numero} de ${nombreMes}: ${texto || 'nadie'}${manual ? ', puesto a mano' : ''}${esHoy ? ', hoy' : ''}`}
        title={texto || undefined}
        className={`relative flex min-h-[2.5rem] w-full min-w-0 flex-col items-center gap-0.5 rounded pt-0.5 pb-1 text-xs tabular-nums transition-colors cursor-pointer
          ${elegido ? fondo : 'text-[#13231E]/80'}
          ${solo && solo.tipo ? 'hover:brightness-110' : 'hover:bg-[#13231E]/[0.06] dark:hover:bg-white/10'}
          ${esHoy ? 'ring-2 ring-offset-1 ring-offset-background ring-[#13231E] dark:ring-white' : ''}`}
      >
        <span>{numero}</span>
        {!elegido && conTipo.length > 0 && (
          <span className="flex max-w-full flex-wrap justify-center gap-0.5" aria-hidden="true">
            {chips.map((e) => (
              <span key={e.user_id} className={`rounded-[3px] px-[3px] text-[8px] font-semibold leading-[11px] ${claseTipo(e.tipo)}`}
                data-testid={`aus-chip-${f}-${e.user_id}`} data-origen={e.origen || undefined}>
                {inicialesDe(e.user_id)}
              </span>
            ))}
            {sobran > 0 && (
              <span className="rounded-[3px] bg-[#13231E]/10 px-[3px] text-[8px] font-semibold leading-[11px] text-[#13231E] dark:bg-white/15">
                +{sobran}
              </span>
            )}
          </span>
        )}
        {manual && (
          <span
            className={`absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full ${solo.tipo ? 'bg-white' : 'bg-[#13231E] dark:bg-white'}`}
            aria-hidden="true"
            data-testid={`aus-manual-${f}`}
          />
        )}
      </button>
    );
  };

  return (
    <div className="space-y-4" data-testid="aus-tab">
      <p className="text-sm text-muted-foreground">
        Vacaciones, teletrabajo y días libres del equipo no médico (los doctores, en Agenda ▸
        Agendas disponibles). Pincha un día para ver quién tiene qué
        {puedeApuntar ? ' y apuntarlo' : ''}.
        {!puedeGestionar && yo ? ' Puedes apuntar tus propios días; los de otra persona, administración.' : ''}
      </p>
      <p className="text-xs text-muted-foreground" data-testid="aus-aviso-cuadrante">
        Lo pintado en el Cuadrante también se ve aquí; las vacaciones restantes del Cuadrante solo
        cuentan lo del Cuadrante.
      </p>

      {/* Persona, año y botones */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-3">
        <div className="w-full sm:w-72">
          <Label htmlFor="aus-persona">Persona</Label>
          <select
            id="aus-persona"
            value={elegido ? elegido.id : ''}
            onChange={(e) => setFiltro(e.target.value)}
            className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
            data-testid="aus-persona"
          >
            <option value="">Todo el equipo</option>
            {miembros.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
          </select>
        </div>
        <NavegacionAnio anio={anio} anioHoy={anioHoy} prefijo="aus" onCambiar={setAnio} />
        {puedeApuntar && (
          <div className="grid grid-cols-2 gap-2 sm:flex sm:ml-auto">
            <Button variant="outline" className="h-10" onClick={() => setRango(rangoVacio(hoy, porDefecto()))}
              data-testid="aus-rango-nuevo">
              <CalendarPlus className="w-4 h-4 mr-1.5" /> Marcar varios días
            </Button>
            <Button variant="outline" className="h-10" onClick={abrirAltaPatron} data-testid="aus-patron-nuevo">
              <Plus className="w-4 h-4 mr-1.5" /> Añadir patrón
            </Button>
          </div>
        )}
      </div>

      {/* Leyenda */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-[#13231E]" data-testid="aus-leyenda">
        {TIPOS.map((t) => <Muestra key={t} tipo={t}>{TEXTO_TIPO[t]}</Muestra>)}
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-4 h-4 rounded ring-2 ring-[#13231E] dark:ring-white" aria-hidden="true" />
          Hoy
        </span>
        {elegido && (
          <span className="inline-flex items-center gap-1.5">
            <span className="relative inline-block w-4 h-4 rounded border border-[#D9E3DD] bg-white" aria-hidden="true">
              <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-[#13231E] dark:bg-white" />
            </span>
            Puesto a mano (no sigue el patrón)
          </span>
        )}
        {resumen && (
          <span className="sm:ml-auto text-muted-foreground" data-testid="aus-resumen">
            {anio}: {resumen.vacaciones} de vacaciones · {resumen.teletrabajo} de teletrabajo
            {' · '}{resumen.dia_libre} {resumen.dia_libre === 1 ? 'día libre' : 'días libres'}
          </span>
        )}
      </div>

      {/* Quién es cada inicial (con todo el equipo a la vista) */}
      {!elegido && miembros.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="aus-miembros">
          {miembros.map((m) => {
            const r = vigente?.resumen?.[m.id];
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => setFiltro(m.id)}
                title={r ? `${m.nombre} · ${anio}: ${r.vacaciones} de vacaciones, ${r.teletrabajo} de teletrabajo, ${r.dia_libre} libres. Pulsa para ver solo sus días` : m.nombre}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#D9E3DD] bg-white px-2 py-0.5 text-xs text-[#13231E] hover:bg-[#13231E]/[0.04]"
                data-testid={`aus-miembro-${m.id}`}
              >
                <span className="rounded-[3px] bg-[#13231E]/10 px-1 text-[10px] font-semibold dark:bg-white/15">{m.iniciales}</span>
                {m.nombre}
              </button>
            );
          })}
        </div>
      )}

      {!vigente && cargando ? (
        <div className="py-10 text-center text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Cargando el calendario…
        </div>
      ) : !miembros.length && !cargando ? (
        <p className="py-10 text-center text-sm text-muted-foreground" data-testid="aus-sin-equipo">
          No hay nadie en el equipo no médico.
        </p>
      ) : (
        <RejillaAnual anio={anio} prefijo="aus" cargando={cargando} renderDia={renderDia} />
      )}

      {/* Patrones */}
      <section className="rounded-xl border border-[#D9E3DD] bg-white p-3 sm:p-4" data-testid="aus-patrones">
        <div className="mb-2">
          <h2 className="text-base font-semibold text-[#13231E]">Patrones</h2>
          <p className="text-xs text-muted-foreground">
            Rellenan los días solos (por ejemplo, teletrabajo todos los viernes). Un día puesto a mano
            no rompe el patrón.{puedeGestionar ? '' : ' Aquí solo salen los tuyos.'}
          </p>
        </div>
        {!patrones.length ? (
          <p className="text-sm text-muted-foreground py-2" data-testid="aus-sin-patrones">Sin patrones.</p>
        ) : (
          <ul className="divide-y divide-[#D9E3DD]">
            {patrones.map((p) => {
              const activo = p.activo !== false;
              const cambiado = p.actualizado_at && p.actualizado_at !== p.creado_at;
              return (
                <li key={p.id} className={`flex flex-col sm:flex-row sm:items-center gap-2 py-2 ${activo ? '' : 'opacity-60'}`}
                  data-testid={`aus-patron-${p.id}`}>
                  <div className="min-w-0 flex-1 text-sm text-[#13231E]">
                    <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-semibold mr-2 align-middle ${claseTipo(p.tipo)}`}>
                      {TEXTO_TIPO[p.tipo] || p.tipo}
                    </span>
                    <span className="font-medium">{nombreDe(p.user_id, p.user_nombre)}</span>
                    <span> · todos los {textoDias(p.dias_semana)}</span>
                    <span className="text-muted-foreground">
                      {' '}· desde {textoFecha(p.desde)}
                      {p.hasta ? ` hasta ${textoFecha(p.hasta)}` : ' · sin fecha de fin'}
                      {p.nota ? ` · ${p.nota}` : ''}
                      {activo ? '' : ' · Desactivado'}
                    </span>
                    <div className="text-[11px] text-muted-foreground" data-testid={`aus-patron-quien-${p.id}`}>
                      Añadido por {p.creado_por_nombre || '—'}{p.creado_at ? ` el ${diaDe(p.creado_at)}` : ''}
                      {cambiado ? ` · cambiado por ${p.actualizado_por_nombre || '—'} el ${diaDe(p.actualizado_at)}` : ''}
                    </div>
                  </div>
                  {puedeTocar(p.user_id) && (
                    <div className="flex gap-1.5 shrink-0">
                      <Button variant="outline" size="sm" onClick={() => abrirEdicionPatron(p)}
                        data-testid={`aus-patron-editar-${p.id}`}>
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => activarPatron(p, !activo)}
                        data-testid={`aus-patron-${activo ? 'desactivar' : 'activar'}-${p.id}`}>
                        <Power className="w-3.5 h-3.5 mr-1" /> {activo ? 'Desactivar' : 'Activar'}
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* La ficha de un día */}
      <Dialog open={!!diaAbierto} onOpenChange={(v) => { if (!v) setDiaAbierto(null); }}>
        <DialogContent className="max-w-md" data-testid="aus-dia-dialogo">
          <DialogHeader>
            <DialogTitle>{diaAbierto ? fechaLarga(diaAbierto) : ''}</DialogTitle>
            <DialogDescription>
              {delDia.some((e) => e.tipo) ? 'Quién tiene algo este día.' : 'Nadie tiene nada este día.'}
            </DialogDescription>
          </DialogHeader>
          {delDia.length > 0 && (
            <ul className="space-y-1" data-testid="aus-dia-lista">
              {delDia.map((e) => (
                <li key={e.user_id} className="flex items-center gap-2 text-sm text-[#13231E]">
                  <span className={`inline-block w-3 h-3 shrink-0 rounded ${e.tipo ? claseTipo(e.tipo) : 'border border-[#D9E3DD]'}`} aria-hidden="true" />
                  <span className="min-w-0">
                    <span className="font-medium">{nombreDe(e.user_id)}</span> — {estadoTexto(e)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {puedeApuntar && (
            <div className="space-y-2 rounded-md border border-dashed border-[#D9E3DD] p-3" data-testid="aus-dia-apuntar">
              <Label htmlFor="aus-dia-persona">Apuntar para</Label>
              <ElegirPersona id="aus-dia-persona" personas={editables} valor={paraQuien}
                onCambiar={setParaQuien} testId="aus-dia-persona" />
              {paraQuien && (
                <p className="text-xs text-muted-foreground" data-testid="aus-dia-ahora">
                  Ahora: {estadoTexto(deLaPersona)}
                  {deLaPersona?.manual ? `. Según el patrón: ${deLaPersona.patron ? TEXTO_TIPO[deLaPersona.patron].toLowerCase() : 'nada'}` : ''}.
                </p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {TIPOS.map((t) => (
                  <Button key={t} disabled={guardandoDia || !paraQuien} onClick={() => apuntarDia(t)}
                    className="justify-center text-white" style={{ backgroundColor: COLOR_TIPO[t] }}
                    data-testid={`aus-opcion-${t}`}>
                    {TEXTO_TIPO[t]}
                  </Button>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button variant="outline" disabled={guardandoDia || !paraQuien} onClick={() => apuntarDia(NINGUNO)}
                  className="justify-start" data-testid="aus-opcion-ninguno">
                  Nada este día
                </Button>
                <Button variant="ghost" disabled={guardandoDia || !deLaPersona?.manual} onClick={() => apuntarDia(null)}
                  className="justify-start" data-testid="aus-opcion-patron">
                  <RotateCcw className="w-4 h-4 mr-1.5" /> Volver al patrón
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Varios días de golpe */}
      <Dialog open={!!rango} onOpenChange={(v) => { if (!v) setRango(null); }}>
        <DialogContent className="max-w-md" data-testid="aus-rango-dialogo">
          <DialogHeader>
            <DialogTitle>Marcar varios días</DialogTitle>
            <DialogDescription>
              Todos los días entre las dos fechas (las dos incluidas) quedan apuntados a mano.
            </DialogDescription>
          </DialogHeader>
          {rango && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="aus-rango-persona">Persona</Label>
                <ElegirPersona id="aus-rango-persona" personas={editables} valor={rango.userId}
                  onCambiar={(v) => setRango((r) => ({ ...r, userId: v }))} testId="aus-rango-persona" />
              </div>
              <fieldset>
                <legend className="text-sm font-medium leading-none">Qué</legend>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5" role="radiogroup">
                  {[...TIPOS, NINGUNO, 'patron'].map((t) => (
                    <label key={t} className="inline-flex items-center gap-2 text-sm text-[#13231E]">
                      <input type="radio" name="aus-rango-tipo" value={t} checked={rango.tipo === t}
                        onChange={() => setRango((r) => ({ ...r, tipo: t }))}
                        className="h-4 w-4 accent-[#13231E]" data-testid={`aus-rango-tipo-${t}`} />
                      {TIPOS.includes(t) && <span className={`inline-block w-3 h-3 rounded ${claseTipo(t)}`} aria-hidden="true" />}
                      {TEXTO_TIPO[t] || (t === NINGUNO ? 'Nada (anula el patrón)' : 'Volver al patrón')}
                    </label>
                  ))}
                </div>
              </fieldset>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="aus-rango-desde">Desde</Label>
                  <Input id="aus-rango-desde" type="date" value={rango.desde} className="mt-1.5"
                    onChange={(e) => setRango((r) => ({ ...r, desde: e.target.value }))}
                    data-testid="aus-rango-desde" />
                </div>
                <div>
                  <Label htmlFor="aus-rango-hasta">Hasta</Label>
                  <Input id="aus-rango-hasta" type="date" value={rango.hasta} className="mt-1.5"
                    onChange={(e) => setRango((r) => ({ ...r, hasta: e.target.value }))}
                    data-testid="aus-rango-hasta" />
                </div>
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-[#13231E]">
                <input type="checkbox" checked={rango.soloLaborables}
                  onChange={(e) => setRango((r) => ({ ...r, soloLaborables: e.target.checked }))}
                  className="h-4 w-4 accent-[#13231E]" data-testid="aus-rango-laborables" />
                Solo de lunes a viernes (sin sábados ni domingos)
              </label>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRango(null)} disabled={guardandoRango}>Cancelar</Button>
            <Button onClick={guardarRango} disabled={guardandoRango} data-testid="aus-rango-guardar">
              {guardandoRango && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alta o edición de un patrón */}
      <Dialog open={!!patron} onOpenChange={(v) => { if (!v) setPatron(null); }}>
        <DialogContent className="max-w-md" data-testid="aus-patron-dialogo">
          <DialogHeader>
            <DialogTitle>{patron?.id ? 'Editar patrón' : 'Añadir patrón'}</DialogTitle>
            <DialogDescription>
              Los días que marques se rellenan solos cada semana.
            </DialogDescription>
          </DialogHeader>
          {patron && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="aus-form-persona">Persona</Label>
                {patron.id ? (
                  <p className="mt-1.5 text-sm font-medium text-[#13231E]" data-testid="aus-form-persona">
                    {nombreDe(patron.userId, patron.userNombre)}
                  </p>
                ) : (
                  <ElegirPersona id="aus-form-persona" personas={editables} valor={patron.userId}
                    onCambiar={(v) => setPatron((f) => ({ ...f, userId: v }))} testId="aus-form-persona" />
                )}
              </div>
              <div>
                <Label>Tipo</Label>
                <div className="mt-1.5 flex flex-wrap gap-2" role="radiogroup" aria-label="Tipo">
                  {TIPOS.map((t) => (
                    <button
                      key={t}
                      type="button"
                      role="radio"
                      aria-checked={patron.tipo === t}
                      onClick={() => setPatron((f) => ({ ...f, tipo: t }))}
                      data-testid={`aus-form-tipo-${t}`}
                      className={`px-3 py-1.5 rounded-md border text-sm font-medium ${
                        patron.tipo === t ? 'text-white' : 'border-[#D9E3DD] text-[#13231E]'}`}
                      style={patron.tipo === t ? { backgroundColor: COLOR_TIPO[t], borderColor: COLOR_TIPO[t] } : undefined}
                    >
                      {TEXTO_TIPO[t]}
                    </button>
                  ))}
                </div>
              </div>
              <fieldset>
                <legend className="text-sm font-medium leading-none">Días de la semana</legend>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1.5">
                  {DIAS_SEMANA.map((nombre, i) => (
                    <label key={nombre} className="inline-flex items-center gap-2 text-sm text-[#13231E]">
                      <input
                        type="checkbox"
                        checked={patron.dias.includes(i)}
                        onChange={() => alternarDiaSemana(i)}
                        className="h-4 w-4 accent-[#13231E]"
                        data-testid={`aus-form-dia-${i}`}
                      />
                      {nombre}
                    </label>
                  ))}
                </div>
              </fieldset>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="aus-form-desde">Desde</Label>
                  <Input id="aus-form-desde" type="date" value={patron.desde} className="mt-1.5"
                    onChange={(e) => setPatron((f) => ({ ...f, desde: e.target.value }))}
                    data-testid="aus-form-desde" />
                </div>
                <div>
                  <Label htmlFor="aus-form-hasta">Hasta (opcional)</Label>
                  <Input id="aus-form-hasta" type="date" value={patron.hasta} className="mt-1.5"
                    onChange={(e) => setPatron((f) => ({ ...f, hasta: e.target.value }))}
                    data-testid="aus-form-hasta" />
                  <p className="text-[11px] text-muted-foreground mt-1">Vacío: sin fecha de fin.</p>
                </div>
              </div>
              <div>
                <Label htmlFor="aus-form-nota">Nota (opcional)</Label>
                <Input id="aus-form-nota" value={patron.nota} maxLength={300} className="mt-1.5"
                  onChange={(e) => setPatron((f) => ({ ...f, nota: e.target.value }))}
                  placeholder="Por ejemplo: acordado hasta verano" data-testid="aus-form-nota" />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPatron(null)} disabled={guardandoPatron}>Cancelar</Button>
            <Button onClick={guardarPatron} disabled={guardandoPatron || !patron?.dias?.length}
              data-testid="aus-form-guardar">
              {guardandoPatron && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {patron?.id ? 'Guardar cambios' : 'Añadir patrón'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
